import {
  addDays,
  addWeeks,
  format,
  getISOWeek,
  getISOWeekYear,
  startOfISOWeek
} from 'date-fns';
import { getLocale, getTrans, TranslationKey } from 'modules/i18n';
import { MINUTES_PER_DAY, MINUTES_PER_WEEK, WEEKDAYS } from './constants';
import {
  AvailabilityDay,
  AvailabilityException,
  AvailabilityExceptionDay,
  IsoWeekOption,
  TimeRange,
  Weekday
} from './types';

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/** Duration in minutes; end at or before start means the range crosses midnight. */
export const rangeDurationMinutes = (range: TimeRange): number => {
  const start = timeToMinutes(range.start);
  const end = timeToMinutes(range.end);
  return end > start ? end - start : end + MINUTES_PER_DAY - start;
};

/**
 * Whether `outer` holds all of `inner`, on the 24-hour circle so a range across midnight (20:00–04:00) holds one inside
 * it (22:00–23:00). pro-core's WeekFitCalculator.covers, which apply v2 uses to keep a profile's preferred range.
 */
export const rangeCovers = (outer: TimeRange, inner: TimeRange): boolean => {
  const offset =
    (timeToMinutes(inner.start) -
      timeToMinutes(outer.start) +
      MINUTES_PER_DAY) %
    MINUTES_PER_DAY;
  return offset + rangeDurationMinutes(inner) <= rangeDurationMinutes(outer);
};

export const totalWeekMinutes = (days: AvailabilityDay[]): number =>
  days.reduce(
    (sum, day) =>
      sum + day.available.reduce((s, r) => s + rangeDurationMinutes(r), 0),
    0
  );

/**
 * Unfolds a day's ranges to absolute week-minute intervals (Monday 00:00 = 0),
 * mirroring the server's overlap rule: a midnight-crossing range continues into
 * the next day (Sunday night wraps to Monday, modulo the week).
 */
const unfoldToWeekIntervals = (
  dayOfWeek: Weekday,
  ranges: TimeRange[]
): Array<[number, number]> => {
  const dayIndex = WEEKDAYS.indexOf(dayOfWeek);
  return ranges.map(range => {
    const start = dayIndex * MINUTES_PER_DAY + timeToMinutes(range.start);
    return [start, start + rangeDurationMinutes(range)];
  });
};

const intervalsOverlap = (intervals: Array<[number, number]>): boolean => {
  // Wrap-around (past Sunday midnight) intervals are split modulo the week.
  const normalized = intervals.flatMap(([start, end]) =>
    end <= MINUTES_PER_WEEK
      ? [[start, end] as [number, number]]
      : [
          [start, MINUTES_PER_WEEK] as [number, number],
          [0, end - MINUTES_PER_WEEK] as [number, number]
        ]
  );
  const sorted = [...normalized].sort((a, b) => a[0] - b[0]);
  return sorted.some((interval, i) => i > 0 && interval[0] < sorted[i - 1][1]);
};

const containedInAny = (
  inner: [number, number],
  outers: Array<[number, number]>
): boolean =>
  outers.some(([start, end]) => inner[0] >= start && inner[1] <= end);

export type DayValidationError =
  | 'range.invalid'
  | 'ranges.overlap'
  | 'preferred.overlap'
  | 'preferred.outside-available';

/**
 * Client-side mirror of the server's per-week range rules. Validates across all
 * days at once so midnight-crossing overlaps into the next day are caught.
 * Returns the first violation or null.
 */
export const validateWeekRanges = (
  days: AvailabilityDay[]
): DayValidationError | null => {
  for (const day of days) {
    for (const range of [...day.available, ...day.preferred]) {
      if (
        !/^\d{2}:\d{2}$/.test(range.start) ||
        !/^\d{2}:\d{2}$/.test(range.end) ||
        range.start === range.end
      ) {
        return 'range.invalid';
      }
    }
  }

  const availableIntervals = days.flatMap(day =>
    unfoldToWeekIntervals(day.dayOfWeek, day.available)
  );
  if (intervalsOverlap(availableIntervals)) return 'ranges.overlap';

  const preferredIntervals = days.flatMap(day =>
    unfoldToWeekIntervals(day.dayOfWeek, day.preferred)
  );
  if (intervalsOverlap(preferredIntervals)) return 'preferred.overlap';

  for (const interval of preferredIntervals) {
    if (!containedInAny(interval, availableIntervals)) {
      return 'preferred.outside-available';
    }
  }

  return null;
};

/** Drops days without any available range (the API's "day absent = unavailable"). */
export const toUpdateDays = (days: AvailabilityDay[]): AvailabilityDay[] =>
  days.filter(day => day.available.length > 0);

/** Sorts a sparse day list into full Monday–Sunday order with empty fallbacks. */
export const toFullWeek = (days: AvailabilityDay[]): AvailabilityDay[] =>
  WEEKDAYS.map(
    weekday =>
      days.find(day => day.dayOfWeek === weekday) ?? {
        dayOfWeek: weekday,
        available: [],
        preferred: []
      }
  );

export const upcomingIsoWeeks = (count: number): IsoWeekOption[] => {
  const currentMonday = startOfISOWeek(new Date());
  return Array.from({ length: count }, (_, i) => {
    const monday = addWeeks(currentMonday, i);
    return {
      year: getISOWeekYear(monday),
      calendarWeek: getISOWeek(monday),
      monday,
      sunday: addDays(monday, 6)
    };
  });
};

export const isoWeekRangeLabel = (option: IsoWeekOption): string => {
  const locale = getLocale();
  return `${format(option.monday, 'dd.MM.', { locale })} – ${format(
    option.sunday,
    'dd.MM.yyyy',
    { locale }
  )}`;
};

export const formatRange = (range: TimeRange): string =>
  `${range.start} – ${range.end}`;

export const weekdayLabel = (weekday: Weekday): string =>
  getTrans(`availabilityPage.weekday.${weekday}` as TranslationKey);

export const formatMinutesAsHours = (minutes: number): string => {
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours}`;
};

export const exceptionByWeek = (
  exceptions: AvailabilityException[] | undefined,
  year: number,
  calendarWeek: number
): AvailabilityException | undefined =>
  exceptions?.find(
    exception =>
      exception.year === year && exception.calendarWeek === calendarWeek
  );

/** Full-week editor rows for an exception: absent day = "as usual". */
export type ExceptionDayMode = 'AS_USUAL' | 'UNAVAILABLE' | 'CUSTOM';

export interface ExceptionDayDraft {
  dayOfWeek: Weekday;
  mode: ExceptionDayMode;
  available: TimeRange[];
  preferred: TimeRange[];
}

export const toExceptionDrafts = (
  days: AvailabilityExceptionDay[] | null | undefined
): ExceptionDayDraft[] =>
  WEEKDAYS.map(weekday => {
    const day = days?.find(d => d.dayOfWeek === weekday);
    if (!day) {
      return {
        dayOfWeek: weekday,
        mode: 'AS_USUAL',
        available: [],
        preferred: []
      };
    }
    return {
      dayOfWeek: weekday,
      mode: day.unavailable ? 'UNAVAILABLE' : 'CUSTOM',
      available: day.available ?? [],
      preferred: day.preferred ?? []
    };
  });

export const toExceptionDays = (
  drafts: ExceptionDayDraft[]
): AvailabilityExceptionDay[] =>
  drafts
    .filter(draft => draft.mode !== 'AS_USUAL')
    .map(draft => ({
      dayOfWeek: draft.dayOfWeek,
      unavailable: draft.mode === 'UNAVAILABLE',
      available: draft.mode === 'CUSTOM' ? draft.available : [],
      preferred: draft.mode === 'CUSTOM' ? draft.preferred : []
    }));
